/**
 * Rotas do app. Cada aba tem um caminho próprio para que recarregar a página
 * (ou compartilhar o link) caia na mesma aba.
 *
 * O rewrite `**` -> /index.html do Firebase Hosting serve qualquer caminho, e
 * o dev server do Vite faz o mesmo, então não precisa de hash na URL.
 */

export type SectionId =
  | 'dashboard'
  | 'expenses'
  | 'reports'
  | 'investments'
  | 'wishlist'
  | 'admin';

export type InvestmentsTabId = 'carteira' | 'operacoes' | 'proventos' | 'irpf';

/** Caminho de cada seção. O dashboard mora na raiz. */
export const SECTION_PATHS: Record<SectionId, string> = {
  dashboard: '/',
  expenses: '/gastos',
  reports: '/relatorios',
  investments: '/investimentos',
  wishlist: '/desejos',
  admin: '/admin',
};

/** Segundo segmento da rota de investimentos. A carteira é a aba padrão. */
export const INVESTMENTS_TABS: InvestmentsTabId[] = [
  'carteira',
  'operacoes',
  'proventos',
  'irpf',
];

export const DEFAULT_INVESTMENTS_TAB: InvestmentsTabId = 'carteira';

export interface Route {
  section: SectionId;
  investmentsTab: InvestmentsTabId;
  /** Caminho não reconhecido: a URL deve ser normalizada. */
  unknown: boolean;
}

function normalizeSegments(pathname: string): string[] {
  return pathname.split('/').filter(Boolean).map((segment) => segment.toLowerCase());
}

export function parseRoute(pathname: string): Route {
  const segments = normalizeSegments(pathname);
  const fallback: Route = {
    section: 'dashboard',
    investmentsTab: DEFAULT_INVESTMENTS_TAB,
    unknown: true,
  };

  if (segments.length === 0) return { ...fallback, unknown: false };

  const entry = (Object.entries(SECTION_PATHS) as [SectionId, string][]).find(
    ([, path]) => path === `/${segments[0]}`
  );
  if (!entry) return fallback;

  const section = entry[0];

  if (section !== 'investments') {
    return {
      section,
      investmentsTab: DEFAULT_INVESTMENTS_TAB,
      unknown: segments.length > 1,
    };
  }

  const tab = segments[1];
  if (!tab) {
    return { section, investmentsTab: DEFAULT_INVESTMENTS_TAB, unknown: false };
  }

  const known = INVESTMENTS_TABS.find((candidate) => candidate === tab);
  return {
    section,
    investmentsTab: known ?? DEFAULT_INVESTMENTS_TAB,
    unknown: !known || segments.length > 2,
  };
}

export function buildPath(section: SectionId, investmentsTab?: InvestmentsTabId): string {
  if (section !== 'investments') return SECTION_PATHS[section];
  const tab = investmentsTab ?? DEFAULT_INVESTMENTS_TAB;
  return tab === DEFAULT_INVESTMENTS_TAB
    ? SECTION_PATHS.investments
    : `${SECTION_PATHS.investments}/${tab}`;
}
